import { FieldType } from '@wix/wix-data-schema-types'
import {
  string,
  number,
  optional,
  boolean,
  objectProperties,
  list,
  object,
  array,
  oneOfShape,
} from './utils/validate'

// Validators for the value shapes used by type metadata properties,
// mirroring the value types from @wix/wix-data-schema-types.

const textValidationConstraintSchema = objectProperties({
  minLength: optional(number),
  maxLength: optional(number),
  pattern: optional(string),
})

const enumValuesConstraintSchema = objectProperties({
  values: list(string),
})

const textFieldValidationSchema = oneOfShape(
  'text validation or enum values',
  textValidationConstraintSchema,
  enumValuesConstraintSchema,
)

const dateValueSchema = oneOfShape(
  'date value',
  objectProperties({ fixed: string }),
  objectProperties({ offsetFromNowInDays: number }),
)

const datetimeValueSchema = oneOfShape(
  'datetime value',
  objectProperties({ fixed: string }),
  objectProperties({ offsetFromNowInSeconds: number }),
)

export const typeMetadataSchemas = {
  [FieldType.text]: {
    defaultValue: optional(string),
    validation: optional(textFieldValidationSchema),
  },
  [FieldType.number]: {
    defaultValue: optional(number),
    min: optional(number),
    max: optional(number),
  },
  [FieldType.boolean]: {
    defaultValue: optional(boolean),
  },
  [FieldType.date]: {
    defaultValue: optional(dateValueSchema),
    minDate: optional(dateValueSchema),
    maxDate: optional(dateValueSchema),
  },
  [FieldType.dateTime]: {
    defaultValue: optional(datetimeValueSchema),
    minDate: optional(datetimeValueSchema),
    maxDate: optional(datetimeValueSchema),
  },
  [FieldType.image]: {
    defaultValue: optional(string),
  },
  [FieldType.document]: {
    defaultValue: optional(string),
  },
  [FieldType.url]: {
    defaultValue: optional(string),
    textValidation: optional(textValidationConstraintSchema),
  },
  [FieldType.email]: {
    defaultValue: optional(string),
  },
  [FieldType.richText]: {
    defaultValue: optional(string),
    minLength: optional(number),
    maxLength: optional(number),
  },
  [FieldType.video]: {
    defaultValue: optional(string),
  },
  [FieldType.mediaGallery]: {
    defaultValue: optional(list(object)),
  },
  [FieldType.address]: {
    defaultValue: optional(object),
  },
  [FieldType.stringArray]: {
    defaultValue: optional(list(string)),
    minSize: optional(number),
    maxSize: optional(number),
    minLengthForItem: optional(number),
    maxLengthForItem: optional(number),
  },
  [FieldType.documentArray]: {
    defaultValue: optional(list(string)),
    minSize: optional(number),
    maxSize: optional(number),
  },
  [FieldType.audio]: {
    defaultValue: optional(string),
  },
  [FieldType.time]: {
    defaultValue: optional(string),
    minTime: optional(string),
    maxTime: optional(string),
  },
  [FieldType.richContent]: {
    defaultValue: optional(object),
  },
  [FieldType.color]: {
    defaultValue: optional(string),
  },
  [FieldType.array]: {
    defaultValue: optional(array),
    minSize: optional(number),
    maxSize: optional(number),
  },
  [FieldType.object]: {
    defaultValue: optional(object),
  },
  [FieldType.slug]: {
    defaultValue: optional(string),
    minLength: optional(number),
    maxLength: optional(number),
  },
  [FieldType.mediaImage]: {
    defaultValue: optional(object),
  },
}

export const withTypeMetadata = <T extends Record<string, unknown>>(
  baseSchema: T,
  fieldType: keyof typeof typeMetadataSchemas,
) => ({
  ...baseSchema,
  ...typeMetadataSchemas[fieldType],
})
